import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { locationsApi } from '@/features/locations/api'
import { objectsApi } from '../api'
import { MapPin, Minimize2, Maximize2, Fullscreen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { buildAppImageUrl } from '@/lib/imagePaths'
import { useCan } from '@/hooks/useCan'

interface FloorPlanViewTabProps {
    objectID?: string | null
    locationID?: number | null
    floorPlanX?: number | null
    floorPlanY?: number | null
}

export function FloorPlanViewTab({ objectID, locationID, floorPlanX, floorPlanY }: FloorPlanViewTabProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const imageRef = useRef<HTMLDivElement>(null)
    const [markerPos, setMarkerPos] = useState<{ x: number; y: number } | null>(
        floorPlanX != null && floorPlanY != null ? { x: floorPlanX, y: floorPlanY } : null
    )
    const [isDirty, setIsDirty] = useState(false)
    // Placing the object on the plan changes it: objects.update. Without it the plan is view-only.
    const canPlace = useCan('objects.update')
    const [viewSize, setViewSize] = useState<'compact' | 'normal'>('compact')
    const containerRef = useRef<HTMLDivElement>(null)

    const toggleFullscreen = useCallback(() => {
        if (!containerRef.current) return
        if (document.fullscreenElement) {
            document.exitFullscreen()
        } else {
            containerRef.current.requestFullscreen()
        }
    }, [])

    // Sync marker position from props when they change
    useEffect(() => {
        if (floorPlanX != null && floorPlanY != null) {
            setMarkerPos({ x: floorPlanX, y: floorPlanY })
        }
    }, [floorPlanX, floorPlanY])

    // Fetch location details to get the image
    const { data: locationData } = useQuery({
        queryKey: ['location-detail', locationID],
        queryFn: async () => {
            if (!locationID) return null
            const res = await locationsApi.get(locationID)
            return (res.data as any)?.data || res.data
        },
        enabled: !!locationID,
    })

    const imageFilename = locationData?.imageFilename || null

    // Save marker mutation
    const saveMutation = useMutation({
        mutationFn: () => {
            if (!objectID) throw new Error('No object ID')
            return objectsApi.updateMarker(objectID, markerPos?.x ?? null, markerPos?.y ?? null)

        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objects', objectID] })
            setIsDirty(false)
            toast({ title: 'Saved', description: 'Marker position saved.' })
        },
        onError: (err: any) => {
            toast({ title: 'Error', description: err.message, variant: 'destructive' })
        },
    })

    // Handle click on image to place/move marker
    const handleImageClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!imageRef.current || !canPlace) return
        const rect = imageRef.current.getBoundingClientRect()
        const x = ((e.clientX - rect.left) / rect.width) * 100
        const y = ((e.clientY - rect.top) / rect.height) * 100
        setMarkerPos({ x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 })
        setIsDirty(true)
    }, [canPlace])

    if (!locationID) {
        return (
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                <p className="text-muted-foreground">No location assigned to this object.</p>
                <p className="text-sm text-muted-foreground mt-1">Assign a location in the object settings to see the floor plan.</p>
            </div>
        )
    }

    if (!imageFilename) {
        return (
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                <p className="text-muted-foreground">No floor plan image uploaded for this location.</p>
                <p className="text-sm text-muted-foreground mt-1">Upload a floor plan or diagram in the Locations settings.</p>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h3 className="text-sm font-medium">{t('objects.floorPlan', 'Floor Plan')}</h3>
                    <p className="text-xs text-muted-foreground">Click on the image to place or move the object marker.</p>
                </div>
                <div className="flex items-center gap-2">
                    {markerPos && (
                        <span className="text-xs text-muted-foreground">
                            {markerPos.x.toFixed(1)}%, {markerPos.y.toFixed(1)}%
                        </span>
                    )}
                    {isDirty && (
                        <Button
                            size="sm"
                            onClick={() => saveMutation.mutate()}
                            disabled={saveMutation.isPending}
                        >
                            {saveMutation.isPending ? 'Saving...' : 'Save Position'}
                        </Button>
                    )}
                    <div className="flex rounded-md border bg-muted/30 p-0.5 gap-0.5">
                        <button
                            type="button"
                            onClick={() => setViewSize('compact')}
                            className={`p-1.5 rounded transition-colors ${viewSize === 'compact' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            title="Compact"
                        >
                            <Minimize2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewSize('normal')}
                            className={`p-1.5 rounded transition-colors ${viewSize === 'normal' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            title="Normal"
                        >
                            <Maximize2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={toggleFullscreen}
                            className="p-1.5 rounded transition-colors text-muted-foreground hover:text-foreground"
                            title="Full screen"
                        >
                            <Fullscreen className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            </div>

            <div
                ref={containerRef}
                className="bg-background"
            >
                <div
                    ref={imageRef}
                    className={`relative rounded-lg border overflow-hidden ${canPlace ? 'cursor-crosshair' : ''}`}
                    style={viewSize === 'compact' ? { maxHeight: '350px' } : {}}
                    onClick={handleImageClick}
                >
                    <img
                        src={buildAppImageUrl('locations', imageFilename ?? '')}
                        alt="Floor plan"
                        className={`object-contain ${viewSize === 'compact' ? 'w-full max-h-[350px]' : 'w-full'}`}
                        draggable={false}
                    />

                {/* Marker */}
                {markerPos && (
                    <div
                        className="absolute pointer-events-none"
                        style={{
                            left: `${markerPos.x}%`,
                            top: `${markerPos.y}%`,
                            transform: 'translate(-50%, -100%)',
                        }}
                    >
                        <MapPin className="h-8 w-8 text-red-600 drop-shadow-lg" fill="currentColor" strokeWidth={1.5} stroke="white" />
                    </div>
                )}
            </div>
            </div>
        </div>
    )
}
